import CheckIcon from '@mui/icons-material/Check';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import CloseIcon from '@mui/icons-material/Close';
import { EditIcon } from '../../../vectors/index';
import { useEditTemplate } from '../hooks/useEditTemplate';
import BundledEditor from '@utils/BundledEditor';
import { EnterpriseTemplate } from '../types';
import { getStrippedText } from '@utils/helper';
import { yupResolver } from '@hookform/resolvers/yup';
import AssistantIcon from '@mui/icons-material/Assistant';
import { AITooltipText } from '@features/messaging';
import { HelpOutline } from '@mui/icons-material';
import AIPromptModal from '@features/messaging/components/AIPromptModal/AIPromptModal';

type TemplateCompanyNameModalProps = {
  isOpen: boolean;
  label: string;
  type: string;
  handleClose: () => void;
  formDataKey: string;
  defaultValue: string | null | undefined;
  templateData: EnterpriseTemplate;
  showEditor?: boolean;
  showTextArea?: boolean;
};

type FormData = {
  [key: string]: string;
};

const TemplateCompanyNameModal: FC<TemplateCompanyNameModalProps> = ({
  templateData,
  label,
  type,
  isOpen,
  handleClose,
  formDataKey,
  defaultValue,
  showEditor,
  showTextArea=false
}) => {
  const [errorText, setErrorText] = useState('');
  const [showAiPromptModal, setShowAiPromptModal] = useState(false);
  const { editTemplate, isUpdatingTemplate } = useEditTemplate({
    setErrorText,
  });

  const schema = yup.object().shape({
    [formDataKey]: yup
      .string()
      .required(`${label} is required`)
      .test('not-empty-html', `${label} cannot be empty`, (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      })
      .test('custom-validation', `Invalid ${label}`, function (value) {
        if (!value) return false;

        if (formDataKey === 's_website_url') {
          const urlRegex = /^(https?:\/\/)?([\w\d\-]+\.){1,}[a-z]{2,}(\/.*)?$/i;
          return urlRegex.test(value);
        }

        if (formDataKey === 'ein') {
          return /^\d{9}$/.test(value ?? '');
        }

        return true;
      }),
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    //@ts-ignore
    defaultValues: {
      [formDataKey]: defaultValue,
    },
  });

  const handleInsertAIContent = (content: string) => {
    setValue(formDataKey, content);
    setShowAiPromptModal(false);
  };
  const onSubmit = (values: FormData) => {
    const options = {
      onSuccess: (data: { message: string }) => {
        toast.success(data.message);
        reset();
        handleClose();
      },
    };
    editTemplate({ ...values, template_id: templateData?.id }, options);
  };

  useEffect(() => {
    if (defaultValue) {
      setValue(formDataKey, defaultValue);
    }
  }, [defaultValue, formDataKey, setValue]);
  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="xs"
        disableEnforceFocus
        fullWidth
      >
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          <br />
          <EditIcon />
          <br />
          <Typography variant="h4">{label}</Typography>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack
              spacing={3}
              sx={{
                pt: 3,
                '& p': {
                  ml: 1,
                  fontSize: '15px',
                },
              }}
            >
              {errorText && <Alert severity="error">{errorText}</Alert>}
              <Controller
                control={control}
                name={formDataKey}
                rules={{
                  required: `${label} is required`,
                  ...(type === 'email' && {
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Enter a valid email address',
                    },
                  }),
                }}
                render={({ field }) =>
                  showEditor ? (
                    <Stack spacing={1}>
                      <BundledEditor
                        {...field}
                        onEditorChange={(newValue: string) =>
                          field.onChange(newValue)
                        }
                        onChange={() => {}}
                        init={{
                          height: 200,
                          menubar: false,
                          plugins: ['lists', 'autolink', 'link', 'pagebreak'],
                          toolbar:
                            'spellchecker language spellcheckdialog | styles | bold italic | bullist numlist | link',
                          placeholder: 'Hiring Culture',
                          spellchecker_dialog: true,
                          spellchecker_rpc_url: 'localhost/ephox-spelling',
                          spellchecker_language: 'en_US',
                          spellchecker_active: true,
                          browser_spellcheck: true,
                          contextmenu: false,
                        }}
                      />
                      {Boolean(errors?.[formDataKey]?.message) && (
                        <Typography color="error">
                          {errors?.[formDataKey]?.message}
                        </Typography>
                      )}
                      {formDataKey === 'our_hiring_culture' && (
                        <Stack
                          mt={1}
                          spacing={2}
                          direction="row"
                          alignItems="center"
                          justifyContent="end"
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setShowAiPromptModal(true)}
                            startIcon={<AssistantIcon />}
                          >
                            Write with AI
                          </Button>
                          <Tooltip title={AITooltipText} placement="top" arrow>
                            <HelpOutline color="secondary" fontSize="small" />
                          </Tooltip>
                        </Stack>
                      )}
                    </Stack>
                  ) : (
                    <TextField
                      {...field}
                      hiddenLabel
                      label={label}
                      variant="outlined"
                      size="small"
                      fullWidth
                      type={type}
                      error={Boolean(errors[formDataKey])}
                      helperText={errors?.[formDataKey]?.message ?? ''}
                      InputProps={{
                        inputProps: {
                          style: {
                            fontSize: '15px',
                          },
                        },
                      }}
                      InputLabelProps={{
                        style: {
                          fontSize: '15px',
                        },
                      }}
                      FormHelperTextProps={{
                        style: {
                          fontSize: '13px',
                        },
                      }}
                      multiline={showTextArea}
                      rows={showTextArea ? 4 : 1}
                    />
                  )
                }
              />

              <LoadingButton
                type="submit"
                variant="contained"
                size="small"
                fullWidth
                loading={isUpdatingTemplate}
                disabled={isUpdatingTemplate}
                loadingPosition="start"
                color="secondary"
                startIcon={<CheckIcon />}
                style={{
                  marginTop: '20px',
                  marginBottom: '10px',
                  fontSize: '16px',
                  padding: '7px 15px',
                }}
              >
                Save
              </LoadingButton>
            </Stack>
          </form>
        </DialogContent>
      </Dialog>
      {showAiPromptModal && (
        <AIPromptModal
          isOpen={showAiPromptModal}
          handleInsertAIContent={handleInsertAIContent}
          handleClose={() => setShowAiPromptModal(false)}
        />
      )}
    </>
  );
};

export default TemplateCompanyNameModal;